import React, { useEffect, useState } from 'react';
import { IAuditLogItemResponse } from 'models/auditLog';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Stack, Typography } from '@mui/material';
import { ERouteLinks } from 'models/route';
import Grid from '@mui/material/Grid';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { AuditLogModules } from 'pages/AuditLogPage/helpers';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import { IEntryData } from 'models/galery';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';

interface IPhotoGalleryEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderFields {
  data: IEntryData[];
  dateFormat: string;
  t: any;
}

enum EType {
  ART = 'ART',
  ANCILLARY = 'ANCILLARY',
  FRT = 'FRT',
  FIREARM = 'FIREARM',
  INDIVIDUAL = 'INDIVIDUAL',
}

const RenderFields = ({ t, data, dateFormat }: IRenderFields) => {
  if (!data.length) return '-';
  return (
    <Grid sx={{ gap: "4px" }}>
      <Grid size={{ xs: 12 }} container>

        <Stack sx={{ width: "25px" }}></Stack>

        <Stack sx={{ width: "150px" }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('manageImages.image.createDate.label')}
          </Typography>
        </Stack>

        <Grid size={{ xs: 6 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('manageImages.image.fileName.label')}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
      {data.map((entry) => (
        <Grid size={{ xs: 12 }} container>

          <Stack sx={{ width: "25px" }}>
            <Typography variant="body2">{Number(entry.position) + 1}</Typography>
          </Stack>

          <Stack sx={{ width: "150px" }}>
            <Typography variant="body2">
              {formatDate(entry.createdDate, dateFormat)}
            </Typography>
          </Stack>

          <Grid size={{ xs: 6 }}>
            <Stack>
              <Typography variant="body2">{entry.filename}</Typography>
            </Stack>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

const PhotoGalleryEventDetails = ({ auditLog }: IPhotoGalleryEventDetails) => {
  const { longDateFormat, getProperties } = useGlobalProperty();
  const navigate = useNavigate();
  const { t } = useTranslations();

  const [navigateToDetails, setNavigateToDetails] = useState<string>('');
  const [ownerObjectId, setOwnerObjectId] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [ancillaryType, setAncillaryType] = useState<string>('');
  const [entries, setEntries] = useState<IEntryData[]>();
  const [previousEntries, setPreviousEntries] = useState<IEntryData[]>();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    const fetchEntries = async (): Promise<void> => {
      if (auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        if (previousObjectValue.entries) {
          setPreviousEntries(
            previousObjectValue.entries.sort(
              (a: any, b: any) => Number(a.position) - Number(b.position),
            ),
          );
        }

        setOwnerObjectId(previousObjectValue.ownerObjectId);
        setAncillaryType(previousObjectValue.ancillaryType);
        if (AuditLogModules.LEGAL_ENTITIES) {
          setType(previousObjectValue.type);
        }
      }

      if (auditLog?.postObjectValue) {
        const postObjectValue = auditLog.postObjectValue as any;
        setOwnerObjectId(postObjectValue.ownerObjectId);
        setAncillaryType(postObjectValue.ancillaryType);
        if (postObjectValue.entries) {
          setEntries(
            postObjectValue.entries.sort(
              (a: any, b: any) => Number(a.position) - Number(b.position),
            ),
          );
        } else {
          setEntries([postObjectValue]);
        }
        if (AuditLogModules.LEGAL_ENTITIES) {
          setType(postObjectValue.type);
        }
      }
    };
    fetchEntries();
  }, [auditLog]);

  useEffect(() => {
    switch (type) {
      case EType.ANCILLARY:
        setNavigateToDetails(
          `${ERouteLinks.ViewAncillary.replace(':id', ownerObjectId).replace(':type', ancillaryType)}`,
        );
        break;
      case EType.ART:
        setNavigateToDetails(
          `${ERouteLinks.ViewAncillaries.replace(':id', ownerObjectId).replace(':type', ancillaryType)}`,
        );
        break;
      case EType.FIREARM:
        setNavigateToDetails(
          `${ERouteLinks.ViewRegisteredFirearm.replace(':id', ownerObjectId)}`,
        );
        break;
      case EType.FRT:
        setNavigateToDetails(`${ERouteLinks.FRTDetails.replace(':frtId', ownerObjectId)}`);
        break;
      case EType.INDIVIDUAL:
        setNavigateToDetails(
          `${ERouteLinks.ViewLegalEntity.replace(':id', ownerObjectId)}?type=${ELegalEntityTypes.individual}`,
        );
        break;
      default:
        break;
    }
  }, [type, ancillaryType]);

  return (
    <>
      {!!previousEntries && !!previousEntries.length && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${auditLog.module} ${t('auditLogDetails.before.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={`${auditLog.eventName}`}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => navigate(navigateToDetails)}
          >
            <RenderFields t={t} data={previousEntries} dateFormat={longDateFormat} />
          </DetailsTemplate>
        </>
      )}

      {entries && (
        <>
          <AuditLogEventName sx={{ marginTop: previousEntries?.length ? '20px' : 0 }}>
            <Typography variant="h5">
              {`${auditLog.module} ${t('auditLogDetails.after.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={`${auditLog.eventName}`}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => navigate(navigateToDetails)}
          >
            <RenderFields t={t} data={entries} dateFormat={longDateFormat} />
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default PhotoGalleryEventDetails;
